import { Col, Row } from 'antd';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { type StockInventorySpecsFragment } from '../../../api/fragments/StockInventorySpecs';
import { ReservationStockStatus } from '../../../api/types';
import CompanyCurrencyField from '../../../components/fields/CompanyCurrencyField';
import MultiPictureWallUploadField from '../../../components/fields/MultiPictureWallUploadField';
import CollapsibleWrapper, { Panel } from '../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../themes/admin/Fields/FormFields';
import useSystemOptions from '../../../utilities/useSystemOptions';
import useSystemSwitchData from '../../../utilities/useSystemSwitchData';
import { useInventory } from '../InventoryDetailsPage/InventoryContext';

const allowedImageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.svg'];
type InventoryStockDetailsSectionProps = {
    offset?: string;
    disabled?: boolean;
};
const InventoryStockDetailsSection = ({ offset, disabled = false }: InventoryStockDetailsSectionProps) => {
    const { t } = useTranslation(['inventoryDetails']);

    const inventory = useInventory();

    const { values } = useFormikContext<{ stock: StockInventorySpecsFragment }>();

    const { stockStatuses } = useSystemOptions();
    const { yesNoSwitch } = useSystemSwitchData();

    return (
        <CollapsibleWrapper defaultActiveKey={t('inventoryDetails:sections.stockDetails')}>
            <Panel
                key={t('inventoryDetails:sections.stockDetails')}
                header={t('inventoryDetails:sections.stockDetails')}
            >
                <Row gutter={14}>
                    <Col lg={8} xs={24}>
                        <FormFields.InputField
                            {...t('inventoryDetails:stocks.identifier', { returnObjects: true })}
                            name="stock.identifier"
                            disabled
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <FormFields.InputField
                            {...t('inventoryDetails:stocks.vin', { returnObjects: true })}
                            disabled={disabled}
                            name="stock.vin"
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <CompanyCurrencyField
                            {...t('inventoryDetails:stocks.price', { returnObjects: true })}
                            company={inventory.module.company}
                            disabled={values.stock.__typename !== 'MobilityStockInventory' || disabled}
                            name="stock.price"
                        />
                    </Col>
                    <Col lg={8} xs={24}>
                        <FormFields.SelectField
                            {...t('inventoryDetails:stocks.status', { returnObjects: true })}
                            disabled={
                                values.stock.__typename === 'MobilityStockInventory' ||
                                [ReservationStockStatus.Reserved, ReservationStockStatus.SystemDeducted].includes(
                                    values.stock.reservationStatus
                                ) ||
                                disabled
                            }
                            name="stock.reservationStatus"
                            options={stockStatuses}
                            showSearch
                        />
                    </Col>
                    {inventory.__typename === 'MobilityInventory' && (
                        <>
                            <Col lg={8} xs={24}>
                                <MultiPictureWallUploadField
                                    {...t('inventoryDetails:stocks.images', { returnObjects: true })}
                                    disabled={disabled}
                                    extensions={allowedImageExtensions}
                                    name="stock.images"
                                />
                            </Col>
                            <Col lg={8} xs={24}>
                                <FormFields.RangePickerField
                                    {...t('inventoryDetails:stocks.period', { returnObjects: true })}
                                    disabled={disabled}
                                    name="stock.period"
                                />
                            </Col>
                            <Col lg={8} xs={24}>
                                <FormFields.SwitchField
                                    {...t('inventoryDetails:stocks.isActive', { returnObjects: true })}
                                    disabled={disabled}
                                    name="stock.isActive"
                                    {...yesNoSwitch}
                                    required
                                />
                            </Col>
                            <Col lg={8} xs={24}>
                                <FormFields.InputNumberField
                                    {...t('inventoryDetails:stocks.mileage', { returnObjects: true })}
                                    addonAfter="km"
                                    disabled={disabled}
                                    name="stock.mileage"
                                />
                            </Col>
                        </>
                    )}
                    <Col lg={8} xs={24}>
                        <FormFields.InputField
                            {...t('inventoryDetails:fields.lastModified', { returnObjects: true, offset })}
                            name="lastModified"
                            disabled
                        />
                    </Col>
                </Row>
            </Panel>
        </CollapsibleWrapper>
    );
};

export default InventoryStockDetailsSection;
